import { Subscription } from 'rxjs';
import { _teamSettingsService, TeamSettingsService } from './services/team-settings.service';

/**
 * The version this browser session is serving, decided once and then held. `undefined` means "not
 * decided yet" — before the boot fetch lands, and again after a logout.
 */
let pinnedIsLegacy: boolean | undefined;
/**
 * The first answer actually handed to a caller this session (dev override excluded). This is what
 * the mounted UI was built from, so it — not the settings-derived pin — is the session's version:
 * a caller served 1.0 before the settings landed must keep getting 1.0, and a later 2.0 flag is a
 * pending flip with the reload notice, not a silent mid-session switch.
 */
let servedIsLegacy: boolean | undefined;
let teamSettingsSubscription: Subscription | undefined;

/**
 * Which Activate UI a team is served: **1.0** (the legacy in-ETL reverse-ETL under `/api/config`)
 * or **2.0** (the standalone activate-service under `/h20/api/config/v2.0/activate`).
 *
 * Driven by the per-team `h2_activation_enabled` setting, so opting a team in — or rolling it
 * back — is a backend flag flip, not a frontend deploy. Teams without the flag stay on 1.0, which
 * makes the old experience the default: shipping this code changes nothing until a team is opted in.
 *
 * Meant to be **sunsetted**: once every team is on 2.0, delete this module and the `-v2` fork gates
 * that read it, then remove the frozen `containers/activate/` + `containers/activate-target/` (1.0)
 * folders.
 *
 * SCOPE: this gate decides **which UI renders**, nothing else. It is deliberately NOT wired into
 * `AppConfig`/transport: `getActivationsURL()` and `getActivationRunsURL()` stay pointed at the ETL
 * `/api/config` backend for everyone, because they are also consumed by non-Activate surfaces
 * (destination overview's delete guard, global search, workflows, scheduler). Activate 2.0 reaches
 * its own service through `activateServiceConfig` instead. Moving those shared getters onto
 * the 2.0 backend is a separate, independently-testable change — see the note in
 * `docs/activate-migration/`.
 *
 * Resolved from the team settings the dashboard's boot guards fetch — not at module load — so a
 * fresh login is picked up without a reload. Any state where the settings are not available yet —
 * service not constructed, fetch failed, logged out — resolves to 1.0.
 *
 * PINNED FOR THE SESSION. The answer is decided the first time the settings are available and then
 * held until logout, so every caller in a session gets the same version even if the flag is flipped
 * on the backend while the tab is open. Sampling it live instead produced a **mixed-version
 * session**: the routes below fork once when `DashboardRoutes` renders, but the global-search
 * registries fork per call, so a refetch landing after a flip left search returning 2.0 hits — whose
 * exit URLs carry the primary `id` — into a still-mounted 1.0 app, which reads that identifier as a
 * `seq_id` and lands on the wrong Activation or a 404. A flip now takes effect on the next full
 * load; `isActivateVersionFlipPending()` drives the notice that asks for one.
 */
export function isLegacyActivateCurrentTeam(): boolean {
  const devOverride = readDevOverride();

  if (devOverride !== undefined) {
    return !devOverride;
  }

  pinToCurrentSession();

  /* The first real answer latches: whatever was served once is what this session serves. */
  if (servedIsLegacy === undefined) {
    /* Undecided — settings not loaded, or logged out — serves 1.0. */
    servedIsLegacy = pinnedIsLegacy ?? true;
  }

  return servedIsLegacy;
}

/**
 * True once the team's live setting disagrees with what this session is *serving*, i.e. the flag
 * changed — or first arrived saying otherwise — after the mounted UI was decided. Nothing switches
 * on its own; a full load is what adopts the new version, so this exists to tell the user that.
 *
 * Compared against the served version, not the settings-derived pin: a boot where the settings were
 * late (failed fetch, slow guard) serves 1.0 before any pin exists, and settings that then arrive
 * enabling 2.0 must surface the reload notice rather than silently agreeing with a pin the mounted
 * routes never saw.
 */
export function isActivateVersionFlipPending(): boolean {
  if (readDevOverride() !== undefined || servedIsLegacy === undefined) {
    return false;
  }

  const teamSettingsService = _teamSettingsService as TeamSettingsService;

  /* No settings to compare against (logged out, failed refetch) is not a flip. */
  return !!teamSettingsService?.getTeamSettingsValue() && servedIsLegacy !== !teamSettingsService.isH2ActivationEnabled();
}

/**
 * Pins on the first settings the session sees, and unpins on logout so the next login decides for
 * its own team — logging out is a client-side navigation, not a page load, so this module state
 * outlives the session that set it.
 *
 * The subscription is what makes the pin land: the settings usually arrive after the first read, and
 * the `BehaviorSubject` replays, so subscribing pins immediately when they are already there.
 */
function pinToCurrentSession(): void {
  const teamSettingsService = _teamSettingsService as TeamSettingsService;

  /* Injector not up yet. Left unsubscribed on purpose, so a later call still gets to pin. */
  if (!teamSettingsService) {
    return;
  }

  if (!teamSettingsSubscription) {
    teamSettingsSubscription = teamSettingsService.teamSettings$.subscribe(teamSettings => {
      if (!teamSettings) {
        /* Logout releases both the pin and the served answer, so the next login — possibly a
           different team — decides afresh instead of inheriting this session's version. */
        pinnedIsLegacy = undefined;
        servedIsLegacy = undefined;
        return;
      }

      if (pinnedIsLegacy === undefined) {
        /* Read back through the service so the setting's key lives in exactly one place. */
        pinnedIsLegacy = !teamSettingsService.isH2ActivationEnabled();
      }
    });
  }
}

/**
 * Local-development flip, so 2.0 can be worked on before any environment sets the team flag:
 *
 *     localStorage.setItem('h2ActivationEnabled', 'true')   // 2.0
 *     localStorage.setItem('h2ActivationEnabled', 'false')  // 1.0
 *     localStorage.removeItem('h2ActivationEnabled')        // back to the team setting
 *
 * Deliberately localStorage rather than a source edit or a config file: nothing lands in the
 * working tree, so it cannot be committed by accident. `process.env.NODE_ENV` is statically
 * replaced at build time, so this whole branch is dead code stripped from production bundles —
 * it can never become a way to self-serve into 2.0 in production.
 */
function readDevOverride(): boolean | undefined {
  if (process.env.NODE_ENV === 'production') {
    return undefined;
  }

  try {
    const raw = localStorage.getItem('h2ActivationEnabled');

    return raw === null ? undefined : raw === 'true';
  } catch {
    return undefined;
  }
}
